import { useCallback, useEffect, useMemo, useState } from "react";
import { FiArrowLeft, FiSave } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { apiRequest } from "../../../services/api";

const emptyForm = {
  name: "",
  productCode: "",
  slug: "",
  status: "DRAFT",
  brandId: "",
  categoryId: "",
  subCategoryId: "",
  unitId: "",
  seasonId: "",
  description: "",
  shortDescription: "",
  fullDescription: "",
  hsnCodeId: "",
  gstSlabId: "",
  trackInventory: true,
  allowNegativeStock: false,
  autoReorderAlerts: true,
  variantLevelTracking: true,
  isFeatured: false,
  isNewArrival: false,
  isBestSeller: false,
  isOnlineVisible: false,
  allowReviews: true,
};

const optionalText = (value) => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

const asList = (response) =>
  Array.isArray(response) ? response : response?.data || [];

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatRuleGroup = (value) =>
  String(value || "APPAREL")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatValueBand = (minimumValue, maximumValue) => {
  const minimum =
    minimumValue === null || minimumValue === undefined
      ? null
      : Number(minimumValue);

  const maximum =
    maximumValue === null || maximumValue === undefined
      ? null
      : Number(maximumValue);

  if (minimum !== null && maximum !== null) {
    return `${formatMoney(minimum)} – ${formatMoney(maximum)}`;
  }

  if (minimum !== null) {
    return `${formatMoney(minimum)} and above`;
  }

  if (maximum !== null) {
    return `Up to ${formatMoney(maximum)}`;
  }

  return "All item values";
};

const buildGstRuleGroups = (slabs) =>
  Object.values(
    (slabs || []).reduce((groups, slab) => {
      const ruleGroup = slab.ruleGroup || "GENERAL";
      groups[ruleGroup] ||= { ruleGroup, bands: [] };
      groups[ruleGroup].bands.push(slab);
      return groups;
    }, {}),
  )
    .map((group) => ({
      ...group,
      bands: [...group.bands].sort(
        (left, right) =>
          Number(left.minimumItemValue ?? 0) -
          Number(right.minimumItemValue ?? 0),
      ),
    }))
    .sort((left, right) => left.ruleGroup.localeCompare(right.ruleGroup));

const formatGstBands = (bands) =>
  bands
    .map(
      (slab) =>
        `Purchase ${formatValueBand(slab.minimumItemValue, slab.maximumItemValue)} · Sale ${formatValueBand(slab.saleMinimumItemValue, slab.saleMaximumItemValue)}: ${Number(slab.rate).toFixed(2)}%`,
    )
    .join(" · ");

export default function ProductEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [form, setForm] = useState(emptyForm);
  const [masters, setMasters] = useState({
    brands: [],
    categories: [],
    subcategories: [],
    units: [],
    seasons: [],
    hsnCodes: [],
    gstSlabs: [],
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadPage = useCallback(async () => {
    if (!accessToken || !id) return;

    try {
      setLoading(true);
      setError("");

      const [
        product,
        brands,
        categories,
        subcategories,
        units,
        seasons,
        hsnCodes,
        gstSlabs,
      ] = await Promise.all([
        apiRequest(`/products/${id}`, { accessToken }),
        apiRequest("/brands", { accessToken }),
        apiRequest("/categories", { accessToken }),
        apiRequest("/subcategories", { accessToken }),
        apiRequest("/units", { accessToken }),
        apiRequest("/seasons", { accessToken }),
        apiRequest("/tax-catalog/hsn-codes?activeOnly=true", {
          accessToken,
        }),
        apiRequest("/tax-catalog/gst-slabs?activeOnly=true", {
          accessToken,
        }),
      ]);

      setMasters({
        brands: asList(brands),
        categories: asList(categories),
        subcategories: asList(subcategories),
        units: asList(units),
        seasons: asList(seasons),
        hsnCodes: asList(hsnCodes),
        gstSlabs: asList(gstSlabs),
      });

      setForm({
        name: product.name || "",
        productCode: product.productCode || "",
        slug: product.slug || "",
        status: product.status || "DRAFT",
        brandId: product.brandId || "",
        categoryId: product.categoryId || "",
        subCategoryId: product.subCategoryId || "",
        unitId: product.unitId || "",
        seasonId: product.seasonId || "",
        description: product.description || "",
        shortDescription: product.shortDescription || "",
        fullDescription: product.fullDescription || "",
        hsnCodeId: product.hsnCodeId || "",
        gstSlabId: product.gstSlabId || "",
        trackInventory: product.trackInventory ?? true,
        allowNegativeStock: product.allowNegativeStock ?? false,
        autoReorderAlerts: product.autoReorderAlerts ?? true,
        variantLevelTracking: product.variantLevelTracking ?? true,
        isFeatured: product.isFeatured ?? false,
        isNewArrival: product.isNewArrival ?? false,
        isBestSeller: product.isBestSeller ?? false,
        isOnlineVisible: product.isOnlineVisible ?? false,
        allowReviews: product.allowReviews ?? true,
      });
    } catch (requestError) {
      setError(requestError.message || "Unable to load product.");
    } finally {
      setLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;
    loadPage();
  }, [accessToken, isAuthLoading, loadPage]);

  const filteredSubcategories = useMemo(() => {
    if (!form.categoryId) return masters.subcategories;

    return masters.subcategories.filter(
      (subcategory) => subcategory.categoryId === form.categoryId,
    );
  }, [form.categoryId, masters.subcategories]);

  const selectedGstSlab = useMemo(
    () => masters.gstSlabs.find((slab) => slab.id === form.gstSlabId) || null,
    [form.gstSlabId, masters.gstSlabs],
  );

  const gstRuleGroups = useMemo(
    () => buildGstRuleGroups(masters.gstSlabs),
    [masters.gstSlabs],
  );

  const selectedGstRuleGroup = useMemo(
    () =>
      gstRuleGroups.find(
        (group) => group.ruleGroup === selectedGstSlab?.ruleGroup,
      ) || null,
    [gstRuleGroups, selectedGstSlab],
  );

  const updateForm = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      setError("Product name is required.");
      return;
    }

    if (!form.unitId) {
      setError("Product unit is required.");
      return;
    }

    if (!form.hsnCodeId) {
      setError("Please select an HSN code.");
      return;
    }

    if (!form.gstSlabId) {
      setError("Please select a GST rule group.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
        name: form.name.trim(),
        productCode: optionalText(form.productCode)?.toUpperCase(),
        slug: optionalText(form.slug),
        status: form.status,
        brandId: form.brandId || undefined,
        categoryId: form.categoryId || undefined,
        subCategoryId: form.subCategoryId || undefined,
        unitId: form.unitId,
        seasonId: form.seasonId || undefined,
        description: optionalText(form.description),
        shortDescription: optionalText(form.shortDescription),
        fullDescription: optionalText(form.fullDescription),
        hsnCodeId: form.hsnCodeId,
        gstSlabId: form.gstSlabId,
        trackInventory: form.trackInventory,
        allowNegativeStock: form.allowNegativeStock,
        autoReorderAlerts: form.autoReorderAlerts,
        variantLevelTracking: form.variantLevelTracking,
        isFeatured: form.isFeatured,
        isNewArrival: form.isNewArrival,
        isBestSeller: form.isBestSeller,
        isOnlineVisible: form.isOnlineVisible,
        allowReviews: form.allowReviews,
      };

      await apiRequest(`/products/${id}`, {
        method: "PATCH",
        body: payload,
        accessToken,
      });

      navigate(`/admindashboard/masters/products/${id}`);
    } catch (requestError) {
      setError(requestError.message || "Unable to update product.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || isAuthLoading) {
    return (
      <div className="py-12 text-center text-gray-500">Loading product…</div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-start gap-4">
        <button
          type="button"
          onClick={() => navigate(`/admindashboard/masters/products/${id}`)}
          className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-gray-600 shadow-sm transition hover:bg-gray-100"
          aria-label="Back to product"
        >
          <FiArrowLeft />
        </button>

        <div>
          <p className="text-sm text-gray-500">
            Masters / Products / Edit Product
          </p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Edit Product
          </h1>

          <p className="mt-2 text-gray-500">
            Update product information and catalogue settings.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSave}
        className="overflow-hidden rounded-3xl border border-gray-100 bg-white"
      >
        <div className="p-6 md:p-8">
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          <h2 className="mb-6 text-xl font-bold text-gray-900">
            Product Information
          </h2>

          <div className="grid gap-5 md:grid-cols-2">
            <Input
              label="Product Name *"
              value={form.name}
              onChange={(value) => updateForm("name", value)}
            />

            <Input
              label="Product Code"
              value={form.productCode}
              onChange={(value) =>
                updateForm("productCode", value.toUpperCase())
              }
            />

            <Input
              label="URL Slug"
              value={form.slug}
              onChange={(value) => updateForm("slug", value)}
            />

            <Select
              label="Status"
              value={form.status}
              onChange={(value) => updateForm("status", value)}
              options={[
                ["DRAFT", "Draft"],
                ["ACTIVE", "Active"],
                ["INACTIVE", "Inactive"],
                ["ARCHIVED", "Archived"],
              ]}
            />

            <Select
              label="Brand"
              value={form.brandId}
              onChange={(value) => updateForm("brandId", value)}
              placeholder="Select brand"
              options={masters.brands.map((item) => [item.id, item.name])}
            />

            <Select
              label="Category"
              value={form.categoryId}
              onChange={(value) => {
                updateForm("categoryId", value);
                updateForm("subCategoryId", "");
              }}
              placeholder="Select category"
              options={masters.categories.map((item) => [item.id, item.name])}
            />

            <Select
              label="Subcategory"
              value={form.subCategoryId}
              onChange={(value) => updateForm("subCategoryId", value)}
              placeholder="Select subcategory"
              options={filteredSubcategories.map((item) => [
                item.id,
                item.name,
              ])}
            />

            <Select
              label="Unit *"
              value={form.unitId}
              onChange={(value) => updateForm("unitId", value)}
              placeholder="Select unit"
              options={masters.units.map((item) => [
                item.id,
                `${item.name}${item.symbol ? ` (${item.symbol})` : ""}`,
              ])}
            />

            <Select
              label="Season"
              value={form.seasonId}
              onChange={(value) => updateForm("seasonId", value)}
              placeholder="Select season"
              options={masters.seasons.map((item) => [item.id, item.name])}
            />

            <Select
              label="HSN Code *"
              value={form.hsnCodeId}
              onChange={(value) => updateForm("hsnCodeId", value)}
              placeholder="Select HSN code"
              options={masters.hsnCodes.map((hsn) => [
                hsn.id,
                hsn.description ? `${hsn.code} — ${hsn.description}` : hsn.code,
              ])}
            />

            <div>
              <Select
                label="GST Rule Group *"
                value={selectedGstRuleGroup?.ruleGroup || ""}
                onChange={(ruleGroup) => {
                  const group = gstRuleGroups.find(
                    (item) => item.ruleGroup === ruleGroup,
                  );
                  updateForm("gstSlabId", group?.bands[0]?.id || "");
                }}
                placeholder="Select GST rule group"
                options={gstRuleGroups.map((group) => [
                  group.ruleGroup,
                  `${formatRuleGroup(group.ruleGroup)} · ${group.bands.length} automatic band${group.bands.length === 1 ? "" : "s"}`,
                ])}
              />

              <p className="mt-2 rounded-xl border border-primary-100 bg-primary-50 px-3 py-2 text-xs leading-5 text-primary-900">
                GST is selected automatically from the item’s final discounted
                value at billing.
              </p>
            </div>

            {selectedGstRuleGroup && (
              <div className="md:col-span-2 rounded-2xl border border-primary-100 bg-primary-50 px-5 py-4">
                <p className="text-xs font-bold uppercase tracking-[0.13em] text-primary-700">
                  Selected automatic GST rule
                </p>

                <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-primary-900">
                      {formatRuleGroup(selectedGstRuleGroup.ruleGroup)}
                    </p>
                    <p className="mt-1 text-sm text-primary-800">
                      {formatGstBands(selectedGstRuleGroup.bands)}
                    </p>
                  </div>

                  <span className="rounded-xl bg-white px-3 py-2 text-sm font-bold text-primary-800 shadow-sm">
                    Automatic by final item value
                  </span>
                </div>
              </div>
            )}

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-800">
                Short Description
              </label>

              <textarea
                rows="2"
                value={form.shortDescription}
                onChange={(event) =>
                  updateForm("shortDescription", event.target.value)
                }
                className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none transition focus:border-primary-700"
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-800">
                Description
              </label>

              <textarea
                rows="5"
                value={form.description}
                onChange={(event) =>
                  updateForm("description", event.target.value)
                }
                className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none transition focus:border-primary-700"
              />
            </div>
          </div>

          <h2 className="mb-5 mt-10 text-xl font-bold text-gray-900">
            Product Settings
          </h2>

          <div className="grid gap-4 md:grid-cols-2">
            <Toggle
              label="Track inventory"
              checked={form.trackInventory}
              onChange={(value) => updateForm("trackInventory", value)}
            />

            <Toggle
              label="Allow negative stock"
              checked={form.allowNegativeStock}
              onChange={(value) => updateForm("allowNegativeStock", value)}
            />

            <Toggle
              label="Automatic reorder alerts"
              checked={form.autoReorderAlerts}
              onChange={(value) => updateForm("autoReorderAlerts", value)}
            />

            <Toggle
              label="Variant-level tracking"
              checked={form.variantLevelTracking}
              onChange={(value) => updateForm("variantLevelTracking", value)}
            />

            <Toggle
              label="Featured product"
              checked={form.isFeatured}
              onChange={(value) => updateForm("isFeatured", value)}
            />

            <Toggle
              label="New arrival"
              checked={form.isNewArrival}
              onChange={(value) => updateForm("isNewArrival", value)}
            />

            <Toggle
              label="Best seller"
              checked={form.isBestSeller}
              onChange={(value) => updateForm("isBestSeller", value)}
            />

            <Toggle
              label="Visible on website"
              checked={form.isOnlineVisible}
              onChange={(value) => updateForm("isOnlineVisible", value)}
            />

            <Toggle
              label="Allow customer reviews"
              checked={form.allowReviews}
              onChange={(value) => updateForm("allowReviews", value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-5 md:px-8">
          <button
            type="button"
            onClick={() => navigate(`/admindashboard/masters/products/${id}`)}
            className="h-11 rounded-xl border border-gray-200 px-5 transition hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={saving}
            className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white transition hover:bg-primary-800 disabled:opacity-60"
          >
            <FiSave />
            {saving ? "Saving…" : "Save Product"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-800">
        {label}
      </label>

      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none transition focus:border-primary-700"
      />
    </div>
  );
}

function Select({ label, value, onChange, options, placeholder }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-800">
        {label}
      </label>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none transition focus:border-primary-700"
      >
        {placeholder && <option value="">{placeholder}</option>}

        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-center justify-between rounded-2xl border border-gray-200 p-4 transition hover:border-primary-200">
      <span className="font-medium text-gray-800">{label}</span>

      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-primary-700"
      />
    </label>
  );
}
